import { useState } from 'react';
import PropTypes from 'prop-types';
import { PiX } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import DateInputCore from '#/modules/commonComponents/form/DateInputCore';
import { formatApiDateDisplay, isValidApiDate } from '#/utils/date';

const EMPTY_META = {};

function isCompleteRangeValue(next) {
  return isValidApiDate(next?.from) && isValidApiDate(next?.to);
}

/**
 * 表頭日期篩選（single／range）；成功才 commit API 日期。
 * range 使用 deferred：完整區間須確認；未完成區間不觸發 search。
 */
function ColumnFilterDateInput({ column }) {
  const meta = column.columnDef.meta ?? EMPTY_META;
  const isServerMode = meta.filterMode === 'server';
  const mode = meta.dateFilterMode === 'range' ? 'range' : 'single';
  const filterValue = isServerMode ? meta.filterValue : column.getFilterValue();
  const placeholder = meta.filterPlaceholder ?? '西元年/月/日';

  const externalValue = mode === 'range'
    ? (filterValue ?? { from: '', to: '' })
    : (filterValue ?? '');

  const [prevExternal, setPrevExternal] = useState(externalValue);
  const [localValue, setLocalValue] = useState(externalValue);

  if (mode === 'single') {
    const ext = filterValue ?? '';
    if (ext !== prevExternal) {
      setPrevExternal(ext);
      setLocalValue(ext);
    }
  } else {
    const extKey = `${filterValue?.from ?? ''}|${filterValue?.to ?? ''}`;
    const prevKey = `${prevExternal?.from ?? ''}|${prevExternal?.to ?? ''}`;
    if (extKey !== prevKey) {
      setPrevExternal(filterValue ?? { from: '', to: '' });
      setLocalValue(filterValue ?? { from: '', to: '' });
    }
  }

  function commit(next) {
    if (isServerMode) {
      meta.onServerFilterChange?.(next);
      return;
    }
    column.setFilterValue(next);
  }

  function handleChange(next) {
    if (mode === 'single') {
      setLocalValue(next);
      if (!next) {
        commit(undefined);
        return;
      }
      commit(next);
      return;
    }

    if (!next?.from && !next?.to) {
      setLocalValue({ from: '', to: '' });
      commit(undefined);
      return;
    }

    if (!isCompleteRangeValue(next)) {
      return;
    }

    setLocalValue(next);
    commit(next);
  }

  function handleClear(event) {
    event.stopPropagation();
    setLocalValue(mode === 'range' ? { from: '', to: '' } : '');
    commit(undefined);
  }

  const hasValue = mode === 'range'
    ? Boolean(localValue?.from || localValue?.to)
    : Boolean(localValue);

  return (
    <div className="relative w-full">
      <DateInputCore
        mode={mode}
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
        compact
        commitBehavior={mode === 'range' ? 'deferred' : 'instant'}
        numberOfMonths={mode === 'range' ? 2 : 1}
        className="w-full"
        inputClassName={cn('h-7 text-xs', hasValue && 'pr-14')}
      />
      {hasValue ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-1/2 right-7 size-6 -translate-y-1/2 text-muted-foreground"
          onClick={handleClear}
          onKeyDown={(event) => event.stopPropagation()}
          aria-label="清除篩選"
        >
          <PiX className="size-3.5" aria-hidden />
        </Button>
      ) : null}
      {mode === 'single' && localValue ? (
        <span className="sr-only">
          {formatApiDateDisplay(localValue)}
        </span>
      ) : null}
    </div>
  );
}

ColumnFilterDateInput.propTypes = {
  column: PropTypes.shape({
    columnDef: PropTypes.shape({
      header: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      meta: PropTypes.object,
    }),
    getFilterValue: PropTypes.func,
    setFilterValue: PropTypes.func,
  }).isRequired,
};

export default ColumnFilterDateInput;
